import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { mergeExistingFields } from "helpers/mergeExistingFields";

export interface RiskKycRulesJson {
  amount: string;
  currency: string;
}

export interface RiskKycRulesItem {
  id: number;
  code: string;
  type: KycRulesType;
  title: string;
  description: string;
  amount: string;
  currency: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type RiskKycRulesItemResponse = Omit<RiskKycRulesItem, 'amount' | 'currency'> & {
  configJson: string;
}


export type KycRulesType = "cumulative_deposit" | "cumulative_withdrawal";

interface RiskKycRulesState {
  list: RiskKycRulesItem[];
  activePage: number;
  totalItems: number;
  totalPages: number;
  isListFetched: boolean;
  selectedItems: Array<RiskKycRulesItem['id']>;
  isItemFetching: boolean;
  isItemFetched: boolean;
  draftItem: Partial<RiskKycRulesItem>;
  processingItemId?: number;
  type: KycRulesType[];
}


const initialState: RiskKycRulesState = {
  list: [],
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  isListFetched: false,
  selectedItems: [],
  isItemFetching: false,
  isItemFetched: false,
  processingItemId: undefined,
  draftItem: {},
  type: ["cumulative_deposit", "cumulative_withdrawal"]
};

export interface GetRiskKycRulesListPayload {
  limit?: number;
  offset?: number;
};

export interface GetRiskKycRulesListResponse {
  items: RiskKycRulesItemResponse[];
  totalItems: number;
  totalPages: number;
};

const getRiskKycRules = async (data?: GetRiskKycRulesListPayload) => {
  const searchParams = new URLSearchParams();

  if (!!data?.limit) searchParams.append('limit', data.limit.toString());
  if (!!data?.offset) searchParams.append('offset', data.offset.toString());

  try {
    const result = await instance.get(
      `${apiRoutes.riskKycRulesList}${searchParams.toString() ? `?${searchParams}` : ''}`
    );

    return result.data as GetRiskKycRulesListResponse;
  } catch (error) {
    throw error
  }
};

const getRiskKycRulesList = createAsyncThunk(
  "kyc-rules/list",
  getRiskKycRules
);

const getRiskKycRulesItem = createAsyncThunk(
  "kyc-rules/get",
  async (id: number) => {
    try {
      const result = await instance.get(
        `${apiRoutes.riskKycRulesList}/${id}`
      );

      return result.data as RiskKycRulesItemResponse;
    } catch (error) {
      throw error;
    }
  }
);

const createRiskKycRulesItem = createAsyncThunk(
  "kyc-preset/create",
  async (data: Pick<RiskKycRulesItem, 'title' | 'code' | 'description' | 'isActive' | 'amount' | 'currency'>, { rejectWithValue }) => {
    try {
      const configJson = JSON.stringify({ currency: data.currency, amount: data.amount });
      const { currency, amount, ...rest } = data;
      const result = await instance.post(
        `${apiRoutes.riskKycRulesList}`,
        {
          ...rest,
          configJson
        }
      );

      return result.data as RiskKycRulesItem;
    } catch (error: any) {
      return rejectWithValue(error?.data?.error ?? error?.data ?? error);
    }
  }
);

const updateRiskKycRulesItem = createAsyncThunk(
  "kyc-rules/update",
  async (data: RiskKycRulesItem, { rejectWithValue }) => {
    try {
      const { id, createdAt, updatedAt, currency, amount, ...dataToUpdate } = data;
      const configJson = JSON.stringify({ currency, amount });
      const result = await instance.put(
        `${apiRoutes.riskKycRulesList}/${id}`,
        {
          ...dataToUpdate,
          configJson
        }
      );

      return result.data as RiskKycRulesItem;
    } catch (error: any) {
      return rejectWithValue(error?.data?.error ?? error?.data ?? error);
    }
  }
);

const deleteRiskKycRulesItem = createAsyncThunk(
  "kyc-rules/delete",
  async (id: number) => {
    try {
      const result = await instance.delete(`${apiRoutes.riskKycRulesList}/${id}`);

      return true;
    } catch (error) {
      throw error;
    }
  }
);

export const riskKycRulesSlice = createSlice({
  name: "riskKycRules",
  initialState,
  reducers: {
    handlePaginate: (
      state: RiskKycRulesState,
      action: PayloadAction<number>,
    ) => {
      state.activePage = action.payload;

      state.isListFetched = false;
    },

    handleClearList: (state: RiskKycRulesState) => {
      state.list = [];

      state.activePage = 1;

      state.totalItems = 0;
      state.totalPages = 0;

      state.isListFetched = false;
    },
    handleSelect: (state, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        )
      } else {
        state.selectedItems.push(action.payload);
      }
    },
    handleSelectAll: (state) => {
      state.selectedItems = state.selectedItems.length !== state.list.length
        ? state.list.map((item) => item.id)
        : [];
    },
    handleClearDraftItem: (state: RiskKycRulesState) => {
      state.draftItem = {};
      state.isItemFetched = false;
    },
    handleUpdateDraftItem: (
      state: RiskKycRulesState,
      action: PayloadAction<Partial<RiskKycRulesItem>>
    ) => {
      state.draftItem = {
        ...state.draftItem,
        ...action.payload
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getRiskKycRulesList.pending, (state) => {
      state.isListFetched = false;
    });

    builder.addCase(getRiskKycRulesList.rejected, (state) => {
      state.isListFetched = true;
    });

    builder.addCase(getRiskKycRulesList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;

      if (action.payload?.items.length) {
        state.list = action.payload.items.map(v => {
          if (!v.configJson) {
            return {
              id: v.id,
              title: v.title,
              code: v.code,
              type: v.type,
              amount: '-1',
              currency: 'unknown',
              description: v.description,
              isActive: v.isActive,
              createdAt: v.createdAt,
              updatedAt: v.updatedAt
            }
          }

          const configJson = JSON.parse(v.configJson) as RiskKycRulesJson;
          
          return {
            id: v.id,
            title: v.title,
            code: v.code,
            type: v.type,
            amount: configJson.amount,
            currency: configJson.currency,
            description: v.description,
            isActive: v.isActive,
            createdAt: v.createdAt,
            updatedAt: v.updatedAt
          }
        });
      }
    });

    builder.addCase(getRiskKycRulesItem.pending, (state) => {
      state.isItemFetching = true;
    });

    builder.addCase(getRiskKycRulesItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.isItemFetched = true;
      const item = action.payload;
      const configJson = item?.configJson ? JSON.parse(item.configJson) : '';

      state.draftItem =  {
        id: item.id,
        title: item.title,
        code: item.code,
        type: item.type,
        amount: configJson ? configJson.amount : '-1',
        currency: configJson ? configJson.currency : 'unknown',
        description: item.description,
        isActive: item.isActive,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt
      }
    });
    
    builder.addCase(getRiskKycRulesItem.rejected, (state) => {
      state.isItemFetching = false;
    });

    builder.addCase(updateRiskKycRulesItem.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });

    builder.addCase(updateRiskKycRulesItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.list = state.list.map((item) =>
        item.id === action.payload.id
          ? mergeExistingFields(item, action.payload)
          : item,
      );
      state.processingItemId = undefined;
    });
    
    builder.addCase(updateRiskKycRulesItem.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });

    builder.addCase(createRiskKycRulesItem.pending, (state) => {
      state.isItemFetching = true;
    });

    builder.addCase(createRiskKycRulesItem.fulfilled, (state) => {
      state.isItemFetching = false;
      state.isListFetched = false;
    });
    
    builder.addCase(createRiskKycRulesItem.rejected, (state) => {
      state.isItemFetching = false;
    });

    builder.addCase(deleteRiskKycRulesItem.pending, (state, action) => {
      state.processingItemId = action.meta.arg;
    });

    builder.addCase(deleteRiskKycRulesItem.fulfilled, (state) => {
      state.processingItemId = undefined;
    });

    builder.addCase(deleteRiskKycRulesItem.rejected, (state) => {
      state.processingItemId = undefined;
    });
  }
});

const {
  handlePaginate,
  handleClearList,
  handleSelect,
  handleSelectAll,
  handleClearDraftItem,
  handleUpdateDraftItem,
} = riskKycRulesSlice.actions;

export {
  handlePaginate,
  handleClearList,
  getRiskKycRulesList,
  getRiskKycRulesItem,
  handleSelect,
  handleSelectAll,
  handleClearDraftItem,
  handleUpdateDraftItem,
  updateRiskKycRulesItem,
  createRiskKycRulesItem,
  deleteRiskKycRulesItem,
  getRiskKycRules
};
